import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Video, Upload, Play, Trash2, Download } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { formatDistanceToNow } from "date-fns";
import { de } from "date-fns/locale";

import { API, BACKEND_URL } from "@/lib/api";

export default function EventVideoUpload({ eventId, isOwner }) {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchVideos();
  }, [eventId]);

  const fetchVideos = async () => {
    try {
      const response = await axios.get(`${API}/events/${eventId}/videos`, { withCredentials: true });
      setVideos(response.data || []);
    } catch (error) {
      console.error("Failed to fetch videos:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check file size (max 100MB)
    if (file.size > 100 * 1024 * 1024) {
      toast.error("Video ist zu groß (max. 100MB)");
      return;
    }

    // Check file type
    if (!file.type.startsWith("video/")) {
      toast.error("Bitte nur Videodateien hochladen");
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    const formData = new FormData();
    formData.append("video", file);

    try {
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      const headers = {
        "Content-Type": "multipart/form-data",
        ...(token ? { "Authorization": `Bearer ${token}` } : {})
      };

      await axios.post(`${API}/events/${eventId}/videos`, formData, {
        withCredentials: true,
        headers,
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      });
      toast.success("Video hochgeladen! 🎬");
      fetchVideos();
    } catch (error) {
      toast.error("Fehler beim Hochladen");
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const deleteVideo = async (videoId) => {
    try {
      await axios.delete(`${API}/events/${eventId}/videos/${videoId}`, { withCredentials: true });
      toast.success("Video gelöscht");
      fetchVideos();
    } catch (error) {
      toast.error("Fehler beim Löschen");
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Upload Section */}
      <div className="bg-white rounded-2xl p-6 shadow-sm">
        <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Video className="w-5 h-5 text-blue-500" />
          Video hochladen
        </h3>

        <input
          ref={fileInputRef}
          type="file"
          accept="video/*"
          onChange={handleUpload}
          className="hidden"
        />

        {uploading ? (
          <div className="space-y-2">
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-blue-500 transition-all duration-300"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
            <p className="text-sm text-gray-500 text-center">{uploadProgress}% hochgeladen</p>
          </div>
        ) : (
          <Button
            onClick={() => fileInputRef.current?.click()}
            variant="outline"
            className="w-full h-24 rounded-xl border-2 border-dashed border-gray-300 hover:border-blue-400 hover:bg-blue-50"
          >
            <div className="flex flex-col items-center gap-2">
              <Upload className="w-6 h-6 text-gray-400" />
              <span className="text-gray-600">Video auswählen (max. 100MB)</span>
            </div>
          </Button>
        )}
      </div>

      {/* Videos List */}
      <div className="space-y-4">
        {videos.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <Video className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p>Noch keine Videos hochgeladen.</p>
          </div>
        ) : (
          videos.map((video) => (
            <div key={video.id} className="bg-white rounded-2xl overflow-hidden shadow-sm">
              <div className="relative aspect-video bg-black">
                <video
                  src={`${BACKEND_URL}${video.url}`}
                  className="w-full h-full object-contain"
                  controls
                  preload="metadata"
                />
              </div>
              <div className="p-4 flex items-center justify-between">
                <div>
                  <p className="font-medium text-gray-900">{video.name}</p>
                  <p className="text-xs text-gray-400">
                    {formatDistanceToNow(new Date(video.created_at), { addSuffix: true, locale: de })}
                  </p>
                </div>
                <div className="flex gap-2">
                  <a
                    href={`${BACKEND_URL}${video.url}`}
                    download
                    className="p-2 text-gray-400 hover:text-blue-500"
                  >
                    <Download className="w-5 h-5" />
                  </a>
                  {isOwner && (
                    <button
                      onClick={() => deleteVideo(video.id)}
                      className="p-2 text-gray-400 hover:text-red-500"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
